import { useQuery } from "@tanstack/react-query";
import { qoUserItemCompletion } from "../../utils/queryOptions";
import { useAuth } from "../../hooks/useAuth";
import { TTask } from "../../types/generalTypes";
import { Check } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const SIZE = 34;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function getColor(score: number): string {
  if (score >= 70) return "var(--color-myGreen)";
  if (score > 30) return "var(--color-myOrange)";
  return "var(--color-myRose)";
}

const ItemCompletion = ({
  data,
  preloadedScore,
}: {
  data: { content_id: number; type: TTask };
  preloadedScore?: number;
}) => {
  const { user } = useAuth();
  const shouldFetch = preloadedScore === undefined;

  const { data: itemCompletion, isSuccess } = useQuery({
    ...qoUserItemCompletion({
      ...data,
      user_id: user!.id,
    }),
    enabled: shouldFetch && !!data.content_id && !!user?.id,
  });

  const score = preloadedScore ?? (isSuccess ? (itemCompletion?.user_scores?.score ?? 0) : null);

  if (score === null) return null;

  const isCompleted = score >= 79;
  const offset = CIRCUMFERENCE - (Math.min(score, 100) / 100) * CIRCUMFERENCE;
  const color = getColor(score);

  if (isCompleted) {
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 12 }}
        className="w-[34px] h-[34px] rounded-full bg-myGreen/15 flex items-center justify-center"
        title="Tamamlandı"
      >
        <motion.div
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 250, damping: 15, delay: 0.1 }}
        >
          <Check size={16} weight="bold" className="text-myGreen" />
        </motion.div>
      </motion.div>
    );
  }

  if (score === 0) {
    return (
      <div className="w-[34px] h-[34px] flex items-center justify-center" title="Henüz başlanmadı">
        <svg width={SIZE} height={SIZE} className="rotate-[-90deg]">
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={STROKE}
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="w-[34px] h-[34px] relative flex items-center justify-center" title={`%${score}`}>
      <svg width={SIZE} height={SIZE} className="rotate-[-90deg]">
        {/* Background circle */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={STROKE}
        />
        {/* Progress circle */}
        <motion.circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          initial={{ strokeDashoffset: CIRCUMFERENCE }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      </svg>
      <span className="absolute text-[11px] font-bold" style={{ color }}>
        {score}
      </span>
    </div>
  );
};

export default ItemCompletion;
